import React from 'react';

import PrivacyPolicy from '@/assets/documents/EU-Privacy-Policy-Tapt-By-Hatch.pdf';
import LayoutStandardNotSticky from '@/components/LayoutStandardNotSticky';

const TermsOfServicePage: React.FC = () => {
  return (
    <LayoutStandardNotSticky heading="Terms of Service">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 bg-white shadow-xl rounded-lg">
        <div className="py-4 grid grid-cols-1 gap-2 text-sm text-gray-600 [&_strong]:font-semibold [&_strong]:text-gray-950 [&_h2]:text-base/7 [&_h2]:font-semibold [&_h2]:text-gray-950 [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:ml-6 [&_ul]:ml-6 [&_li]:py-1">
          <h2 className="line-y py-2">
            <strong>1. Terms</strong>
          </h2>
          <p className="pl-2">
            These Terms and Conditions ("Terms"), together with our Privacy
            Policy, apply to your use of Tapt's Sites located at{' '}
            <a
              href="https://tapt.io/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://tapt.io/
            </a>{' '}
            and{' '}
            <a
              href="https://taptbyhatch.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://taptbyhatch.com/
            </a>{' '}
            ("Sites"), your purchase of our Products and the provision of our
            Services.
            <br />
          </p>
          <p className="pl-2">
            For the purposes of these Terms, "you" includes you in your personal
            capacity as the user of the Sites and Products, and, where you are
            an organisation or corporation that purchases our Products and/or
            Services, that organisation or corporation. "Tapt", "we", "our" or
            "us" refers to: Tapt Limited (company no 15712750 for users based in
            the United Kingdom or Tapt By Hatch's (ACN 638 840 972) for users
            based in Australia or anywhere else world-wide.
          </p>
          <ol>
            <li>
              <p>
                By browsing or using the Sites, you agree to these Terms,
                together with our Privacy Policy (collectively, the
                "Agreement"). If you do not agree with these Terms, you must not
                access, browse or use these Sites or purchase our Products. You
                agree to the obligations imposed on you under these Terms and,
                in exchange, we agree to provide you with access to the Sites,
                Services and Products.
              </p>
            </li>
            <li>
              <p>
                We reserve all rights to alter these Terms at our discretion.
                Each time you use the Sites, it is your responsibility to ensure
                that you are aware of our current Terms. Your continued use of
                the Sites following the publication of changes to these Terms is
                taken to be your acceptance of those changes. These Terms, as
                amended by us from time to time, continue to apply for as long
                as you use the Sites, Products and Services or until terminated
                in accordance with these Terms.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>2. Interpretation</strong>
          </h2>
          <ol>
            <li>
              <p>In these Terms:</p>
              <div className="pl-2">
                <p>
                  <strong>"Account"</strong> means an account automatically or
                  manually created, upon, prior or post purchase of our
                  Products;
                </p>
                <p>
                  <strong>"Agreement"</strong> has the meaning in clause 1.1;
                </p>
                <p>
                  <strong>"Australian Consumer Law"</strong> means Schedule 2 of
                  the Competition and Consumer Act 2010 (Cth);
                </p>
                <p>
                  <strong>"Business Day"</strong> means 9:00am – 5:00pm Monday
                  to Friday, excluding Saturdays, Sundays and public holidays in
                  the relevant jurisdiction in accordance with clause 17.5;
                </p>
                <p>
                  <strong>"Consumer Guarantee"</strong> (a) has the meaning
                  given in Division 1 of Part 3-2 of the Australian Consumer Law
                  for users based in Australia or worldwide except for the UK,
                  or (b) means the statutory rights for consumer in the UK under
                  the Consumer Rights Act 2015 and Consumer Contract Regulations
                  for users based in the UK;
                </p>
                <p>
                  <strong>"Delivery Cost"</strong> means the costs associated
                  with delivery of our Products;
                </p>
                <p>
                  <strong>"Intellectual Property Rights"</strong> means all
                  present and future intellectual or industrial property rights
                  (whether or not registered) throughout the world, including,
                  without limitation, in respect of patents, copyright, moral
                  rights, trade names, trade marks, logos, systems, circuit
                  layout, designs, software, plant breeder's rights, domain
                  names, trade secrets and confidential information;
                </p>
                <p>
                  <strong>"Material"</strong> means any information, data,
                  source codes, drawings, content, text or images in any form
                  (whether visible or not), audio recordings, video recordings,
                  lists, sound, video, links, layout, look and feel, control
                  features and interfaces contained on the Sites, or otherwise
                  displayed, uploaded or published on, or via, the Sites;
                </p>
                <p>
                  <strong>"NFC Technology"</strong> means the Near Frequency
                  Communication which is the technology imbedded in our Products
                  and used to transmit information stored to smart phones;
                </p>
                <p>
                  <strong>"Order"</strong> means an order placed for our
                  Products as offered by us from time to time;
                </p>
                <p>
                  <strong>"Personal Information"</strong> means information or
                  opinion about an identified individual, or an individual who
                  is reasonably identifiable, whether true or not, and whether
                  recorded in a material form or not includes any personal
                  information that you upload to your Tapt Card(s) or our
                  Products;
                </p>
                <p>
                  <strong>"Profile Information"</strong> means the information
                  required to be printed on the corresponding Tapt Card and any
                  files or links uploaded to a Profile which would ordinarily be
                  considered as being protected by your Intellectual Property
                  Rights.
                </p>
                <p>
                  <strong>"Platform"</strong> means access to the dashboard
                  functionality associated with your Account.
                </p>
                <p>
                  <strong>"Platform Utilisation Fee"</strong> has the meaning in
                  clause 7.1.
                </p>
                <p>
                  <strong>"Platform Utilisation Period"</strong> means an annual
                  period (12 months) from when you agree to Purchase the
                  Services and the Platform Utilisation Period End Date.
                </p>
                <p>
                  <strong>"Privacy Policy"</strong> means our privacy policy
                  available{' '}
                  <a
                    href={PrivacyPolicy}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  ;
                </p>
                <p>
                  <strong>"Products"</strong> includes our Tapt Card(s) or any
                  other products, goods or items listed or advertised on the
                  Sites from time to time, which may also include replacement
                  Tapt Cards;
                </p>
                <p>
                  <strong>"Profile"</strong> means the online profile created by
                  you whereby your personal information, cover images, social
                  profile information and other files and quick links can be
                  uploaded and accessed;
                </p>
                <p>
                  <strong>"Profile Holder"</strong> means each person in your
                  organisation to whom a Tapt Card and/or has a digital profile
                  using our Services, where that has occurred at your or your
                  organisation's request. Where you have such a profile and/or
                  Tapt Card, it includes you;
                </p>
                <p>
                  <strong>"Purchase"</strong> means an Order that has been paid
                  by you or an organisational user for our Products and
                  confirmed by us;
                </p>
                <p>
                  <strong>"QR Technology"</strong> means quick response code
                  technology;
                </p>
                <p>
                  <strong>"Services"</strong> means the services listed or
                  advertised on the Sites for sale or otherwise and for the
                  avoidance of doubt includes our ongoing services provided
                  during any Platform Utilisation Period;
                </p>
                <p>
                  <strong>"Sites"</strong> means{' '}
                  <a
                    href="https://tapt.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://tapt.io/
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://taptbyhatch.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://taptbyhatch.com/
                  </a>
                  ;
                </p>
                <p>
                  <strong>"Tapt Card(s)"</strong> means our card(s) that uses
                  NFC Technology and QR Technology to transmit stored
                  information uploaded by you directly to smart phones; and
                </p>
                <p>
                  <strong>"You" or "your"</strong> means the person or entity
                  accessing, using or relying upon the Sites.
                </p>
              </div>
            </li>
            <li>
              <p>
                Any reference in these Terms to the singular includes the
                plural, to any gender includes all genders, to any act or
                statute includes any Act or statute which supersedes, replaces
                or modifies any earlier Act or statute, to persons includes all
                bodies and associations both corporate and incorporated and vice
                versa. Paragraph headings are for reference purposes only and
                all references to clauses are to clauses in these Terms unless
                otherwise specified.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>3. Sites, Tapt Card And Profile Use</strong>
          </h2>
          <ol>
            <li>
              <p>
                You may not use and must procure that each Profile Holder does
                not use the Sites and Products other than for their intended
                purpose. You warrant and represent that access to, or use of,
                the Sites and Products is not unlawful or prohibited by any laws
                which apply to you or your Profile Holders. You understand and
                agree that any suspected fraudulent, abusive, or illegal
                activity may be referred to appropriate law enforcement
                authorities. You agree and acknowledge that a Tapt Card and
                Profile can be used to acquire Personal Information of persons
                with whom Profile Holders interact and you accept all
                responsibility and liability for any access, collection and use
                of such information.
              </p>
            </li>
            <li>
              <p>
                You agree that you will not engage in and will procure that each
                Profile Holder does not engage in any activity that interferes
                with or disrupts the Sites, or the servers and networks that
                host the Sites. You agree not to and will procure that each
                Profile Holder does not circumvent, disable or otherwise
                interfere with security-related features of the Sites or attempt
                to do so or otherwise interfere with, or restrict, any person or
                visitor from accessing or using the Sites.
              </p>
            </li>
            <li>
              <p>
                You agree that you must not and must procure that your Profile
                Holders do not do anything that amounts to:
              </p>
              <ol>
                <li>
                  <p>
                    Compromising the integrity of our systems. This could
                    include probing, scanning, or testing the vulnerability of
                    any system or network that hosts our services;
                  </p>
                </li>
                <li>
                  <p>
                    Tampering with, reverse-engineering, or hacking our Sites,
                    circumventing any security or authentication measures, or
                    attempting to gain unauthorised access to the Sites, related
                    systems, networks, or data;
                  </p>
                </li>
                <li>
                  <p>
                    Modifying, disabling, or compromising the integrity or
                    performance of the Sites or related systems, network or
                    data;
                  </p>
                </li>
                <li>
                  <p>
                    Deciphering any transmissions to or from the servers running
                    the Sites;
                  </p>
                </li>
                <li>
                  <p>
                    Overwhelming or attempting to overwhelm our infrastructure
                    by imposing an unreasonably large load on our systems that
                    consume extraordinary resources (CPUs, memory, disk space,
                    bandwidth, etc.), such as:
                  </p>
                  <ol>
                    <li>
                      <p>
                        Using "robots," "spiders," "offline readers," or other
                        automated systems to send more request messages to our
                        servers than a human could reasonably send in the same
                        period of time by using a normal browser;
                      </p>
                    </li>
                    <li>
                      <p>
                        Going far beyond the use parameters for any given
                        service as described in its corresponding documentation;
                        and
                      </p>
                    </li>
                    <li>
                      <p>
                        Consuming an unreasonable amount of storage for music,
                        videos, and other files in a way unrelated to the
                        purposes for which the Site were designed.
                      </p>
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <p>
                You agree that you must not and must procure that your Profile
                Holders do not do anything that amounts to:
              </p>
              <ol>
                <li>
                  <p>
                    Misrepresenting yourself/themselves, or disguising the
                    origin of any content (including by "spoofing", "phishing",
                    manipulating headers or other identifiers, impersonating
                    anyone else, or falsely implying any sponsorship or
                    association with us or any third party);
                  </p>
                </li>
                <li>
                  <p>
                    Using the Sites or our services to violate the privacy of
                    others, including publishing or posting other people's
                    personal and confidential information without their express
                    permission, or collecting or gathering other people's
                    personal information (including account names or
                    information) from our services;
                  </p>
                </li>
                <li>
                  <p>
                    Using the Sites or our services to stalk, harass, or post
                    direct, specific threats of violence against others;
                  </p>
                </li>
                <li>
                  <p>
                    Using the Sites or our services for any illegal purpose, or
                    in violation of any laws (including without limitation data,
                    privacy, and export control laws);
                  </p>
                </li>
                <li>
                  <p>
                    Accessing or searching any part of the Sites by any means
                    other than our publicly supported interfaces (for example,
                    "scraping");
                  </p>
                </li>
                <li>
                  <p>
                    Using the Sites or our services for the purpose of providing
                    alerts on disaster scenarios or any other situations
                    directly related to health or safety, including but not
                    limited to acts of terrorism, natural disasters, or
                    emergency response;
                  </p>
                </li>
                <li>
                  <p>
                    Using the Sites or our services to generate or send
                    unsolicited communications, advertising, chain letters, or
                    spam;
                  </p>
                </li>
                <li>
                  <p>Disparaging us or our partners, vendors, or affiliates;</p>
                </li>
                <li>
                  <p>
                    Promoting or advertising products or services other than
                    your own without appropriate authorisation;
                  </p>
                </li>
                <li>
                  <p>
                    Posting, uploading, sharing, submitting, or otherwise
                    providing content that:
                  </p>
                  <ol>
                    <li>
                      <p>
                        infringes our or a third party's Intellectual Property
                        Rights or any other intellectual property right or
                        proprietary or contractual right;
                      </p>
                    </li>
                    <li>
                      <p>you do not have the right to submit;</p>
                    </li>
                    <li>
                      <p>
                        is deceptive, fraudulent, illegal, obscene, defamatory,
                        threatening, harmful to minors, pornographic (including
                        child pornography), indecent, harassing, hateful;
                      </p>
                    </li>
                    <li>
                      <p>encourages illegal or tortious conduct;</p>
                    </li>
                    <li>
                      <p>
                        attacks others based on their race, ethnicity, national
                        origin, religion, sex, gender, sexual orientation,
                        disability, or medical condition;
                      </p>
                    </li>
                    <li>
                      <p>
                        contains viruses, bots, worms, scripting exploits, or
                        other similar materials; or
                      </p>
                    </li>
                    <li>
                      <p>is intended to be inflammatory.</p>
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <p>
                We may, in our absolute discretion, terminate your Account,
                disable your Account or restrict your access to the Sites
                (temporarily or permanently) where you have breached our
                Agreement, or may do so in respect of particular Profile
                Holders. Under these circumstances, you or the Profile Holders
                in question may be prevented from accessing all or parts of the
                Sites, our Products, Services and your Account details or any
                other content associated with your Account. We will not be
                liable to you or any third party if this occurs.
              </p>
            </li>
            <li>
              <p>
                We may impose limits or restrictions on the use you and your
                Profile Holders may make of the Sites. Further, we may, for any
                reason, at any time and without notice to you, withdraw the
                Sites, or change or remove Sites functionality
              </p>
            </li>
            <li>
              <p>
                The Sites may contain links to third party sites. Any links to
                such sites provided on our Sites is for convenience only. We do
                not represent that we have any relationship with any linked
                sites nor recommend or endorse any goods, services or third
                party content appearing on, or via, other sites linked to the
                Sites. We are not responsible for any loss or damage that may
                arise from your access to, and/or use of, third party sites,
                products and services. Additionally, we are not responsible for
                the content or privacy practices associated with linked sites.
                You should make your own enquiries before using and/or accessing
                third party sites.
              </p>
            </li>
            <li>
              <p>
                Unless otherwise expressly stipulated in these Terms, you or any
                of your Profile Holders must not copy, adapt, distribute,
                display, reproduce or transmit any content displayed or
                published on the Sites. For the avoidance of doubt, this does
                not include information relating to Profile Holders.
              </p>
            </li>
            <li>
              <p>
                You must take your own precautions to ensure that the
                telecommunications equipment and computer systems used by you to
                access and use the Sites does not expose your telecommunications
                equipment and computer systems to any viruses, malicious
                computer code or other forms of interference which may damage
                your computer system. We accept no responsibility for any loss
                or damage to you or anyone else which may arise out of, or in
                connection with, your access to, and use of, the Sites.
              </p>
            </li>
            <li>
              <p>
                Where required, we will provide you with ongoing customer
                support if you submit your enquiries to{' '}
                <a href="mailto:support@tapt.io">support@tapt.io</a>. We will
                endeavour to respond to any customer support requests within ten
                (10) business days.
              </p>
            </li>
            <li>
              <p>
                Although we endeavour to provide accurate and complete
                information on the Products listed or advertised on the Sites,
                we cannot guarantee the information is up to date, accurate and
                complete at all times. We reserve the right to (but are not
                obligated to) make changes or updates to information displayed
                on the Sites at any time without notice to you.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>4. Orders</strong>
          </h2>
          <ol>
            <li>
              <p>
                Orders can be placed by completing the Order form via the Sites
                and clicking the "confirm" button. We do not guarantee the
                availability of any Products or Services displayed or ordered
                on, or via, the Sites.
              </p>
            </li>
            <li>
              <p>
                An Order is not accepted and legally binding on us until we
                confirm by email that:
              </p>
              <ol>
                <li>
                  <p>payment has been received for the Order;</p>
                </li>
                <li>
                  <p>the Products and/or Services are available; and</p>
                </li>
                <li>
                  <p>the Order has been processed.</p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                Each Order (once confirmed by us in writing) represents a
                separate agreement between the parties (and each separate Order
                placed by you on, or via, the Sites will be subject to this
                Agreement).
              </p>
            </li>
            <li>
              <p>We reserve the right, at our absolute discretion, to:</p>
              <ol>
                <li>
                  <p>
                    refuse to sell or to cancel Orders from Customers that
                    request commercial quantities of Products or Services; or
                  </p>
                </li>
                <li>
                  <p>
                    cancel your Order at any time prior to dispatch of the
                    Products, or the provision of the Services, to you.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                Subject to clause 8, if an Order has been cancelled, refused, or
                cannot be met or replaced due to unavailability, funds paid in
                relation to that Order will be refunded in full as soon as is
                reasonably practicable. You will be provided with email
                acknowledgement of the cancellation and any applicable refund.
              </p>
            </li>
            <li>
              <p>
                We accept no responsibility for Orders that are declined,
                misplaced, not received or not accepted due to disruptions
                caused to our internet connections or our computer systems
                through no fault of our own.
              </p>
            </li>
            <li>
              <p>
                It is your responsibility to ensure that the correct contact
                details are provided and that the nominated email address is
                regularly checked for correspondence.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>5. Your Account And Profile Information</strong>
          </h2>
          <ol>
            <li>
              <p>
                For each Profile Holder, you must complete or procure the
                completion of their Profile with their Profile Information
                before we can begin the fulfilment process in respect of your
                Tapt Cards. We take no responsibility for any delay caused by
                you or any person in your organisation in uploading the Profile
                Information.
              </p>
            </li>
            <li>
              <p>
                Once the Profile Information and any other information necessary
                or relevant to the design of Tapt Cards has been submitted to us
                for printing, we will send up to three (3) emails seeking your
                confirmation of Profile Information and design of card for
                printing prior to proceeding. If we do not receive any
                instructions changing such information after the third email we
                are entitled to assume that the information and design does not
                require correction and will proceed. Up to three design edits
                are included in the initial fee. Additional edits may incur
                additional fees. Once a design and Profile Information is
                submitted, we cannot issue any refund in the event that there
                are errors or misspellings due to you having failed to notify us
                of these earlier.
              </p>
            </li>
            <li>
              <p>
                To the maximum extent permitted by law, Orders placed for a Tapt
                Card(s) cannot be cancelled by you once they have been accepted
                by us and we have uploaded your Profile Information or we have
                begun the printing process, unless as otherwise outlined in
                clause 8.
              </p>
            </li>
            <li>
              <p>
                For the most effective use of our Products, you must and must
                procure that your Profile Holders always keep account details
                current through your Account dashboard. You agree that you have
                sole responsibility for any activity (including by any Profile
                Holder) that occurs on or using your Account or any account
                pertaining to your Profile Holders. You agree to notify us
                immediately if you become aware of any security breach or any
                unauthorised use of your (or any Profile Holder's) Password or
                Account.
              </p>
            </li>
            <li>
              <p>
                Except as set out herein, we take no responsibility for Profile
                Information that contains incorrect information, errors or
                misspelling made by you/your Profile Holders.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>6. Order Payments</strong>
          </h2>
          <ol>
            <li>
              <p>
                You can pay for your Order using any of the methods specified on
                the Sites. A surcharge may apply to payments made by credit card
                depending on the credit card used.
              </p>
            </li>
            <li>
              <p>
                All prices for our Products listed on the Sites and are subject
                to fees and jurisdictionally applicable taxes unless otherwise
                specified. Prices are subject to change without notice however
                we will always to endeavour to provide at least 30 days' notice
                on our Sites before price changes come into effect.
              </p>
            </li>
            <li>
              <p>
                Any discounts from our standard advertised pricing are given at
                our sole discretion on terms and conditions notified at the time
                of the relevant discount. If a discount code is entered at the
                checkout, it has the effect of applying a discount only in
                relation to the sale price of the Products. Any associated
                Delivery Cost, or Platform Utilisation Fee, will be unaffected
                by the discount code and payable at the ordinary rate.
              </p>
            </li>
            <li>
              <p>
                You agree and acknowledge that we will treat an electronic
                instruction as authentic and are under no obligation to
                investigate the authenticity or authority of persons issuing or
                transmitting such electronic instructions, or to verify the
                accuracy and completeness of such electronic instructions.
              </p>
            </li>
            <li>
              <p>
                Payment processing services for the Products and Services are
                provided by Shopify. By using Shopify to process payments, you
                agree to be bound by the{' '}
                <a
                  href="https://www.shopify.com/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shopify Terms of Services
                </a>
                , which may be modified by Shopify from time to time.
              </p>
            </li>
            <li>
              <p>
                If your nominated payment method triggers our internal suspect
                transaction protocols, we may contact you to confirm additional
                details, or rescind the transaction. In this case, until payment
                for your Order has passed our fraud prevention protocols your
                Purchase will not be complete. If you do not provide the
                requested information within seven (7) days, your Order or the
                provision of our Services will be cancelled and your payment
                will be refunded back to you via the method in which you paid.
                These information requests are sent to help protect credit card
                holders from online fraud though we provide no guarantee that we
                will identify any or all instances of online fraud that may
                occur in relation to any Purchase made on or via the Sites.
              </p>
            </li>
            <li>
              <p>
                To the maximum extent permitted by law, we reserve the right to
                refuse a sale to any Customer.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>7. Multi Card Platform Utilisation Periods And Fees</strong>
          </h2>
          <ol>
            <li>
              <p>
                In addition to the terms set out in Clause 6, where you have
                four (4) or more Tapt Cards registered to your Account,{' '}
                <strong>
                  we will charge you an annual 12-month fee for access and use
                  of the Services and Platform during the Platform Utilisation
                  Period (Platform Utilisation Fee)
                </strong>
                .
              </p>
            </li>
            <li>
              <p>
                The Platform Utilisation Fee will be structured into tiers based
                on the number of cards associated with your account. Information
                relating to pricing for our Platform Utilisation Fees are listed
                on the Sites or will otherwise be communicated to you by us in
                writing.
              </p>
            </li>
            <li>
              <p>
                Our Platform Utilisation Fees are subject to jurisdictionally
                applicable taxes unless otherwise specified. While our prices
                are subject to change without notice, we will always endeavour
                to provide at least 30 days' notice on our Sites or to you in
                writing before any price changes come into effect.
              </p>
            </li>
            <li>
              <p>
                In the event that you increase the number cards associated with
                your Account during any Platform Utilisation Period, you will be
                required to pay the difference, on a pro rata basis, between
                your original tier and any new tier attached to the number of
                cards.
              </p>
            </li>
            <li>
              <p>
                The Platform Utilisation Fee will automatically renew for each
                successful 12-month period, and will be charged to your credit
                card immediately prior to the commencement of your next Platform
                Utilisation Period,{' '}
                <strong>
                  unless our Services are terminated by you in accordance with
                  Clause 16
                </strong>
                . For the avoidance of doubt, your credit card will continue to
                be charged at the end of each Platform Utilisation Period for a
                successive one-year period.
              </p>
            </li>
            <li>
              <p>
                Where you are paying us via invoice, we will send you an invoice
                via email within a reasonable time prior to the commencement of
                your next Platform Utilisation Period,{' '}
                <strong>
                  unless our Services are terminated by you in accordance with
                  Clause 16
                </strong>
                . Our invoices are due and payable within 14 days.
              </p>
            </li>
            <li>
              <p>
                To ensure you are aware of the upcoming payment obligations of
                your Platform Utilisation Fee, prior to the end of each Platform
                Utilisation Period, we will send you one (1) reminder email at
                30 days before the automatic renewal takes place and your credit
                card is charged, or your invoice is due.
              </p>
            </li>
            <li>
              <p>
                If you wish to terminate the Services and prevent automatic
                renewal for a successive period, you must terminate your use of
                the Services at least ten (10) days before the end of your
                current Platform Utilisation Period. Failure to do so will
                result in the automatic renewal of the Services for another
                one-year period and you will be liable for payment.
              </p>
            </li>
            <li>
              <p>
                In the event that you fail to pay the Platform Utilisation Fee,
                your card(s) will remain operational for their intended use of
                the Products and Services. However, your access to the Platform
                will not be available until you make full payment.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>8. Refunds</strong>
          </h2>
          <ol>
            <li>
              <p>
                Nothing contained in these Terms excludes, restricts or modifies
                the application of any condition, warranty or other obligation,
                the exercise of any right or remedy, or the imposition of any
                liability under the any applicable laws where to do so is
                unlawful.
              </p>
            </li>
            <li>
              <p>
                To the maximum extent permitted by law, our sole liability for
                breach of any such condition, warranty or other obligation is
                limited to supplying the Platform or Products or Services or
                payment of the cost of having the Platform or Products or
                Services supplied again.
              </p>
            </li>
            <li>
              <p>
                We may (in our absolute discretion) elect to refund the Platform
                Utilisation Fee paid by you in the following circumstances:
              </p>
              <ol>
                <li>
                  <p>
                    if you are cancelling the Services and request a refund
                    within 24 hours of making the payment of the Platform
                    Utilisation Fee; or
                  </p>
                </li>
                <li>
                  <p>
                    if your ability to use the Services is cancelled prior to
                    the end of a Platform Utilisation Period for which you have
                    incurred a charge in error or due to disability or death.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                To the maximum extent permitted by law, we will not provide you
                with a refund, or exchange or repair the Products or Services
                purchased by you on, or via, the Sites where:
              </p>
              <ol>
                <li>
                  <p>
                    we have begun printing the Tapt Card(s) with your details;
                  </p>
                </li>
                <li>
                  <p>
                    Tapt Card(s) or other Products offered by us are damaged
                    through misuse, accident or abnormal use; or
                  </p>
                </li>
                <li>
                  <p>
                    the Australian Consumer Law (for Australian users) or any
                    manufacturer's warranty does not apply.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                Consumer Guarantees cannot be excluded and are in addition to
                any manufacturer's warranties or extended warranties purchased
                or given to you.
              </p>
            </li>
            <li>
              <p>
                If a Tapt Card(s) or other Products offered by us is damaged,
                incorrect or faulty, you should contact us as soon as possible
                at <a href="mailto:support@tapt.io">support@tapt.io</a> to
                arrange your return or exchange. You must comply with directions
                or instructions given by us in relation to returning the
                damaged, faulty or defective Tapt Card(s) or other Products
                however we will always to endeavour to provide at least 30 days'
                notice on our Sites before price changes come into effect to us.
              </p>
            </li>
            <li>
              <p>
                It is your responsibility to ensure that returned Tapt Card(s)
                or other Products are returned safely and within a reasonable
                period of time. We accept no responsibility for Tapt Card(s) or
                other Products lost in transit.
              </p>
            </li>
            <li>
              <p>
                Where you are returning Tapt Card(s) or other Products to us
                because of our failure to comply with a Consumer Guarantee, Tapt
                Card(s) or other Products returned to us will be at our cost.
                Refunds may take up to 10 Business Days to be processed.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>9. Delivery And Ownership Of Products</strong>
          </h2>
          <ol>
            <li>
              <p>
                You agree to pay any Delivery Costs as they are calculated and
                listed in the Order confirmation at the time of purchase.
                International delivery costs do not include insurance or any
                taxes or duties which may be applied by customs at the
                destination country and any duties or taxes incurred are the
                responsibility of the person who placed the Order.
              </p>
            </li>
            <li>
              <p>
                Delivery Costs will vary depending on the Order you have made,
                the method of delivery, the location for delivery and any taxes
                or duties that may be charged in relation to delivery of the
                Order.
              </p>
            </li>
            <li>
              <p>
                In accordance with Australian export regulations, we are
                required to declare the exact value of all items and identify
                the Order as dutiable "merchandise". We are also prohibited by
                law from identifying an Order as a "gift" for export purposes,
                even if you have placed the Order with the intention of delivery
                to a gift recipient.
              </p>
            </li>
            <li>
              <p>
                We will use reasonable endeavours to ensure that all Tapt
                Card(s) or other Products offered by us and Services are
                delivered in a prompt and timely manner. However, delivery dates
                are estimates only and factors outside our control may result in
                delays to delivery. The estimated date of delivery may change
                without notice. We do not accept any liability for loss or
                damage suffered by you or anyone as a result of any such delays.
              </p>
            </li>
            <li>
              <p>
                Where you or any person on your behalf has provided an incorrect
                or incomplete delivery address for your Order and your Order is
                returned to us, we may redeliver the Order at your request and
                charge a redelivery fee for each subsequent delivery attempt.
              </p>
            </li>
            <li>
              <p>
                You acknowledge and agree that it is your responsibility to
                ensure you and any person on your behalf have nominated the
                correct delivery address (residential, email or as otherwise
                applicable) and provided sufficient and appropriate details to
                identify the recipient in making an Order, and we are not
                responsible for any misplaced parcels or for delivery of any
                Order to an unintended recipient as a consequence of incorrect
                details.
              </p>
            </li>
            <li>
              <p>
                You must ensure that any person who collects or takes delivery
                of the Tapt Card(s) or other Products on your behalf is
                authorised by you to do so. Title and risk in Tapt Card(s) or
                other Products will pass to you on your (or someone authorised
                on your behalf) signing for delivery of the Tapt Card(s) or
                other Products. Where you give written authority for Tapt
                Card(s) or other Products to be delivered without a signature:
              </p>
              <ol>
                <li>
                  <p>
                    any and all included insurance cover (if any) will be
                    voided; and
                  </p>
                </li>
                <li>
                  <p>
                    title and risk in the Products pass to the Customer on
                    delivery of the Products to your nominated delivery address.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                We are not responsible for the delivery times of Tapt Card(s) or
                other Products. Once Tapt Card(s) or other Products have been
                dispatched, it is your responsibility to liaise with our
                nominated courier company in relation to date of delivery and to
                make themselves available to take delivery at the nominated time
                for delivery. Any information provided by us to you in relation
                to the method of delivery and estimated delivery time will be
                from a third party, and as such we do not guarantee the accuracy
                or currency of such information. You are also responsible for
                paying any duty or similar tax that may apply to your purchase
                or importation.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>10. Legal Capacity</strong>
          </h2>
          <ol>
            <li>
              <p>
                You must be and must procure that all Profile Holders are
                eighteen (18) years of age or over to register for an Account or
                to Order or receive the Tapt Card(s) or other Products or
                Services on, or via, the Sites. If you are under the age of 18
                years ("Minor"), you must immediately cease accessing and using
                the Sites unless you have permission from a parent or guardian
                to create an Account in accordance with clause 10.3 of these
                Terms. If you are found to be a Minor, we are entitled, at our
                absolute discretion, to cancel or terminate any Order and/or
                purchase of Tapt Card(s) or other Products or Services made on,
                or via, the Sites.
              </p>
            </li>
            <li>
              <p>
                Any Order and/or purchase made by you or any Profile Holder
                using this Sites and your continued use of the Sites is an
                acknowledgement by you that:
              </p>
              <ol>
                <li>
                  <p>
                    you/they are over the age of eighteen (18) years, or have
                    obtained the relevant permission from a parent or guardian
                    to create an Account; and
                  </p>
                </li>
                <li>
                  <p>
                    you accept these Terms and agree that you have entered into
                    a binding legal contract with us in relation to the Terms.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                If you are a parent or guardian permitting a Minor to create an
                Account, you agree to:
              </p>
              <ol>
                <li>
                  <p>
                    ensure payment of any fees or charges owing, including any
                    Platform Utilisation Fee;
                  </p>
                </li>
                <li>
                  <p>exercise supervision over the Minor's use of the Sites;</p>
                </li>
                <li>
                  <p>
                    assume all risks associated with use of the Sites as
                    outlined in these Terms;
                  </p>
                </li>
                <li>
                  <p>
                    ensure that all content and information that the Minor may
                    encounter on the Sites is suitable and appropriate for the
                    Minor;
                  </p>
                </li>
                <li>
                  <p>
                    assume all liabilities resulting from the Minor's use of our
                    Sites and their Account;
                  </p>
                </li>
                <li>
                  <p>
                    ensure the accuracy and truthfulness of all information
                    submitted by the Minor;
                  </p>
                </li>
                <li>
                  <p>
                    provide the consents contained in these Terms on behalf of
                    the Minor.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                We may, at any time, request written confirmation from a parent
                or guardian that you have permission to access and use this
                Sites.
              </p>
            </li>
            <li>
              <p>
                We reserve the right to take legal action and/or seek
                compensation for any loss or damage we may suffer as a result
                of, or in connection with, any transaction entered into by a
                Minor or from the parent or guardian of a Minor who causes an
                Order to be placed.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>11. Intellectual Property Rights</strong>
          </h2>
          <ol>
            <li>
              <p>
                Except in respect of your Personal Information or Profile
                Information uploaded to each Tapt Card(s), you acknowledge that
                we or our licensors are the owners of all Intellectual Property
                Rights in the Sites and in all material published on the Sites,
                and we retain all rights, title and interest in the Sites and
                Material (including Intellectual Property Rights contained
                therein) irrespective of any licence we may grant to you to
                access, and use, the Sites.
              </p>
            </li>
            <li>
              <p>
                Except in respect of your Personal Information and Profile
                Information, by uploading, posting, transmitting or otherwise
                making available any content or material via the Sites ("Your
                Content"), you:
              </p>
              <ol>
                <li>
                  <p>
                    grant to us a non-exclusive, worldwide, royalty-free,
                    perpetual, irrevocable and sub-licensable licence to use,
                    publish, reproduce and otherwise exploit Your Content in any
                    form for any purpose and unconditionally waive any moral
                    rights that you might have in respect of Your Content;
                  </p>
                </li>
                <li>
                  <p>
                    represent and warrant that you either own the Intellectual
                    Property Rights in Your Content or have the necessary
                    permission to upload, post, transmit or otherwise make
                    available Your Content via the Sites; and
                  </p>
                </li>
                <li>
                  <p>
                    agree that we may use examples of your Tapt Card designs (as
                    long as they do not disclose Personal Information) for our
                    promotional and marketing purposes including, without
                    limitation, at our Sites and on social media.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                You must not in any form or by any means reproduce, modify,
                distribute, store, transmit, publish, use or display the
                Material on another Sites or create derivative works from any
                part of the Sites or the Material or commercialise any
                information obtained from any part of the Sites or Material
                without our prior written consent.
              </p>
            </li>
            <li>
              <p>
                We reserve the right to terminate any licence granted to you
                under these Terms and/or remove any of Your Content from the
                Sites, at any time, for any reason and without notice to you.
              </p>
            </li>
            <li>
              <p>
                You agree that you will not modify or copy the layout or
                appearance of the Sites nor any computer software or code
                contained in the Sites, and that you will not decompile,
                disassemble, reverse engineer or otherwise attempt to discover,
                interfere with or access any source code related to the Sites.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>12. Indemnity</strong>
          </h2>
          <ol>
            <li>
              <p>
                You agree to indemnify us and our officers, directors, employers
                or contractors (collectively, the "Indemnified") and to keep
                indemnified and hold harmless the Indemnified from and against
                any and all actions, claims, demands, losses, damages, taxes,
                liabilities, costs and/or expenses that may be incurred by, or
                sustained by, the Indemnified arising out of, or in connection
                with, your access to, and use of, the Materials, the Sites, any
                breach by you of these Terms or your or anyone else's use of the
                Tapt Card(s), Products or Services, unless and to the extent
                that the loss or damage could have been mitigated by us.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>13. Liability</strong>
          </h2>
          <ol>
            <li>
              <p>To the maximum extent permitted by law, we exclude:</p>
              <ol>
                <li>
                  <p>
                    All conditions, guarantees or warranties expressed or
                    implied by law; and
                  </p>
                </li>
                <li>
                  <p>
                    any liability to you or to any third person however arising
                    (and whether arising under statute, negligence or otherwise)
                    for any personal injury or death to you or any third person,
                    or for any special, direct, indirect or consequential loss
                    or damage (including, but not limited to, loss of income or
                    revenue, loss or interruption of business, loss of profits,
                    revenue or contracts, loss of anticipated savings, loss of
                    data, loss of use, loss of privacy or loss of goodwill),
                    arising out of, or in connection with, access and/or use of
                    the Material, the Sites, or any Products or Services ordered
                    on or purchased via the Sites and these Terms.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <p>
                Without limiting the generality of the foregoing, you agree that
                (unless otherwise required by law) in no event shall our maximum
                aggregate liability exceed one (1) Platform Utilisation Fee or
                the cost of the Platform or Products or Services supplied to
                you. You acknowledge and agree the limitations of liability
                contained in this clause are a fair and reasonable allocation of
                the commercial risk between the parties.
              </p>
            </li>
            <li>
              <p>
                To the maximum extent permitted by law, all typographical,
                clerical or other errors or omissions in sales literature,
                quotations, price lists, acceptances or offers, invoices or
                other documents or information issued by us may be subject to
                correction (with prospective effect) without any liability on
                our part.
              </p>
            </li>
            <li>
              <p>
                Nothing contained in these Terms excludes, restricts or modifies
                the application of any condition, warranty or other obligation,
                the exercise of any right or remedy, or the imposition of any
                liability under any applicable laws where to do so is unlawful.
              </p>
            </li>
            <li>
              <p>
                This clause 13 survives termination or expiry of these Terms.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>14. Privacy</strong>
          </h2>
          <ol>
            <li>
              <p>
                We are committed to protecting your privacy and personal
                information. Please see our Privacy Policy for further details
                about our practices relating to the collection, use, disclosure
                and storage of your personal information.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>16. Modern Slavery</strong>
          </h2>
          <ol>
            <li>
              <p>"Modern Slavery" has the same meaning given in the Act; and</p>
            </li>
            <li>
              <p>
                "the Act" refers to (a) the Modern Slavery Act 2018 (Cth) for
                users based in Australia or worldwide except for the UK, or (b)
                the Modern Slavery Act 2015 for users based in the UK.
              </p>
            </li>
            <li>
              <p>For the purposes of this clause:</p>
            </li>
            <li>
              <p>
                We will take reasonable steps to identify, assess and address
                risks of Modern Slavery practices in the operations and supply
                chains used in the provisions of the Products and/or Services
                and otherwise comply with any obligations that we may have under
                the Act.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>17. Termination</strong>
          </h2>
          <ol>
            <li>
              <p>
                Tapt reserves the right to terminate our Services to you at any
                time without cause upon 30 days' prior written notice.
              </p>
            </li>
            <li>
              <p>
                You may terminate the use of Services at any time by providing
                written notice to Tapt at least ten (10) days before the desired
                termination date.
              </p>
            </li>
            <li>
              <p>
                Tapt reserves the right to suspend or terminate access to the
                Platform, Product and Services immediately and without prior
                notice in the event of unauthorised or illegal use, violation of
                intellectual property rights, or any other activity that Tapt
                deems harmful or disruptive to its operations or reputation.
              </p>
            </li>
            <li>
              <p>
                Upon termination of the Services, you shall remain liable for
                any outstanding fees or charges incurred prior to the effective
                date of termination, including any Platform Utilisation Fees.
              </p>
            </li>
            <li>
              <p>
                Termination of the services shall not relieve either party of
                any obligations or liabilities accrued prior to the effective
                date of termination, including but not limited to
                confidentiality obligations, indemnification, and payment of
                outstanding fees.
              </p>
            </li>
            <li>
              <p>
                Tapt reserves the right to retain or delete any data or content
                provided by you within 14 days following the termination of the
                services, in accordance with its data retention policies and
                applicable laws and regulations.
              </p>
            </li>
          </ol>

          <h2 className="line-y py-2">
            <strong>18. General</strong>
          </h2>
          <ol>
            <li>
              <p>
                We reserve the right to make changes to these Terms without
                notice to you. Any amendments to these Terms will have immediate
                effect from the time that they are published on the Sites.
              </p>
            </li>
            <li>
              <p>
                Although we do our best to provide the most up-to-date
                information on the Sites as this becomes available, we cannot
                warrant the accuracy or completeness of the information
                provided.
              </p>
            </li>
            <li>
              <p>
                Any provision of these Terms which is void or unenforceable may
                be severed from these Terms without affecting the enforceability
                of other provisions.
              </p>
            </li>
            <li>
              <p>
                A failure or delay by us to exercise a power or right under
                these Terms does not operate as a waiver of that power or right,
                and the exercise of a power or right by us does not preclude our
                future ability to exercise that or any other power or right.
              </p>
            </li>
            <li>
              <p>
                These Terms are governed by, and must be construed according to,
                the laws of the jurisdiction in which the contracting Tapt
                entity is located, specifically:
              </p>
            </li>
          </ol>

          <div className="my-4">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
                    Tapt entity
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
                    Applicable jurisdiction
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">
                    Tapt Limited (company no 15712750)
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    England and Wales
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">
                    Tapt By Hatch's (ACN 638 840 972)
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    Victoria, Australia
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-4">
            The parties submit to the exclusive jurisdiction of the courts
            exercising jurisdiction there.
          </p>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>support@tapt.io &nbsp; &nbsp; tapt.io</p>
          </div>
        </div>
      </div>
    </LayoutStandardNotSticky>
  );
};

export default TermsOfServicePage;
